import { createElement, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";

/**
 * Path routing over the History API, without a router dependency.
 *
 * Four sections and an editor are reason enough for URLs - a link to a
 * draft or to the register should survive being pasted into an email - but
 * not for a library: the whole need is "which path are we on" and "go to
 * this path", which is this file.
 *
 *   /chat                 /chat/:conversationId
 *   /documents            /documents/models      /documents/:draftId
 *   /compliance           /compliance/obligations /compliance/register
 *   /memory               /memory/:tab
 *
 * Deep links need the server to answer any of these with index.html. Vite's
 * dev server does; in production that is the CloudFront function in
 * platform/infra/frontend.tf.
 */

const CHANGE = "arhilex:navigate";

export function currentPath(): string {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === window.location.pathname + window.location.search) return;
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.dispatchEvent(new Event(CHANGE));
}

/** The current path, re-rendering on navigation and on back/forward. */
export function usePath(): string {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const update = () => setPath(currentPath());
    window.addEventListener("popstate", update);
    window.addEventListener(CHANGE, update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(CHANGE, update);
    };
  }, []);
  return path;
}

/** "/documents/:id" against "/documents/abc" -> { id: "abc" }, else null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const want = pattern.split("/").filter(Boolean);
  const have = path.split("/").filter(Boolean);
  if (want.length !== have.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    if (want[i].startsWith(":")) params[want[i].slice(1)] = decodeURIComponent(have[i]);
    else if (want[i] !== have[i]) return null;
  }
  return params;
}

/**
 * An anchor that navigates in place. A real <a href>, so middle-click,
 * ctrl-click and "copy link" all behave as they do anywhere else.
 */
export function Link({
  to,
  onClick,
  ...rest
}: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  function go(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  }
  return createElement("a", { href: to, onClick: go, ...rest });
}
